import {
  createNavigatorFactory,
  StackActions,
  StackRouter,
  useNavigationBuilder,
  type DefaultNavigatorOptions,
  type EventMapBase,
  type NavigationProp,
  type NavigatorTypeBagBase,
  type ParamListBase,
  type StackActionHelpers,
  type StackNavigationState,
  type StackRouterOptions,
  type StaticConfig,
  type TypedNavigator
} from '@react-navigation/native';
import { type FC, useEffect } from 'react';

import { MEINE_REGION_FEED_ROUTE } from '@/features/geo-area/components/region/regionTypes';
import { MeineRegionScreen } from '@/features/geo-area/screens/MeineRegionScreen';
import type {
  AppTabScreenProps,
  TabParam,
  TabRoute
} from '@/navigation/types/navigation';

export type MeineRegionFeedParam = {
  [MEINE_REGION_FEED_ROUTE]: TabParam[typeof TabRoute.Region];
};

const FeedEntry: FC = () => <></>;

type HistoryProps = DefaultNavigatorOptions<
  ParamListBase,
  undefined,
  StackNavigationState<ParamListBase>,
  object,
  EventMapBase,
  NavigationProp<ParamListBase>
> &
  StackRouterOptions & {
    requestedSelection: TabParam[typeof TabRoute.Region];
  };

const MeineRegionHistoryNavigator: FC<HistoryProps> = ({
  children,
  initialRouteName,
  requestedSelection
}) => {
  const { state, navigation, descriptors, NavigationContent } =
    useNavigationBuilder<
      StackNavigationState<ParamListBase>,
      StackRouterOptions,
      StackActionHelpers<ParamListBase>,
      object,
      EventMapBase
    >(StackRouter, { children, initialRouteName });
  const active = state.routes[state.index];
  const activeSelection =
    active.params as MeineRegionFeedParam[typeof MEINE_REGION_FEED_ROUTE];

  useEffect(() => {
    const currentState = navigation.getState();
    const current = currentState.routes[currentState.index];
    const currentSelection =
      current.params as MeineRegionFeedParam[typeof MEINE_REGION_FEED_ROUTE];
    if (
      requestedSelection &&
      requestedSelection.tab !== currentSelection?.tab
    ) {
      navigation.dispatch({
        ...StackActions.push(MEINE_REGION_FEED_ROUTE, requestedSelection),
        target: state.key
      });
    }
  }, [requestedSelection, navigation, state.key]);

  return (
    <NavigationContent>
      <MeineRegionScreen
        tab={activeSelection?.tab}
        onVisit={(params) => {
          navigation.dispatch({
            ...StackActions.push(MEINE_REGION_FEED_ROUTE, params),
            target: state.key
          });
        }}
      />
      {descriptors[active.key].render()}
    </NavigationContent>
  );
};

type MeineRegionHistoryTypeBag<
  ParamList extends ParamListBase = ParamListBase,
  NavigatorID extends string | undefined = string | undefined
> = {
  ParamList: ParamList;
  NavigatorID: NavigatorID;
  State: StackNavigationState<ParamList>;
  ScreenOptions: object;
  EventMap: EventMapBase;
  NavigationList: {
    [RouteName in keyof ParamList]: NavigationProp<
      ParamList,
      RouteName,
      NavigatorID,
      StackNavigationState<ParamList>,
      object,
      EventMapBase
    > &
      StackActionHelpers<ParamList>;
  };
  Navigator: typeof MeineRegionHistoryNavigator;
};

const createMeineRegionHistory = <
  const ParamList extends ParamListBase,
  const NavigatorID extends string | undefined = undefined,
  const TypeBag extends NavigatorTypeBagBase = MeineRegionHistoryTypeBag<
    ParamList,
    NavigatorID
  >,
  const Config extends StaticConfig<TypeBag> = StaticConfig<TypeBag>
>(
  config?: Config
): TypedNavigator<TypeBag, Config> =>
  createNavigatorFactory(MeineRegionHistoryNavigator)(config) as TypedNavigator<
    TypeBag,
    Config
  >;

const History = createMeineRegionHistory<MeineRegionFeedParam>();

type Props = AppTabScreenProps<typeof TabRoute.Region>;

export const MeineRegionRouting: FC<Props> = ({ route }) => (
  <History.Navigator requestedSelection={route.params}>
    <History.Screen
      name={MEINE_REGION_FEED_ROUTE}
      component={FeedEntry}
      initialParams={route.params}
    />
  </History.Navigator>
);
